import { createAsyncThunk } from '@reduxjs/toolkit';
import Api from 'old-store/utils/API';
import ApiErrors from 'old-store/utils/API/APIErrors';
import { ICheckoutProduct } from 'modules/ScrShopNew/store/slices/checkout/types';
import {
  IShippingReducer,
  UpdateBillingPayload,
  UpdateCreateCustomerPayload
} from 'modules/ScrShopNew/store/slices/shipping/types';
import {
  getFormValues,
  isFormHasError,
  validateFieldStore,
  trackProducts,
  isBillingFormHasError
} from 'modules/ScrShopNew/store/utils/helpers';
import {
  claimCheckoutAction,
  fetchCheckoutAction,
  pruneUnavailableCartItems,
  recalculatePricingAction
} from 'modules/ScrShopNew/store/slices/checkout/actions';
import { fetchProducts } from 'modules/ScrShopNew/store/slices/products/actions';
import { IBillingAddress, setShippingValueAction } from 'modules/ScrShopNew/store/slices/shipping';
import { getTranslationKey } from 'helpers/texting';
import { isNetworkError } from 'helpers/network';
import { getShippingStore } from './selectors';
import { getCheckoutIDSelector, getCheckoutProducts } from '../checkout/selectors';
import { getPaymentMethod } from '../shop/selectors';
import { RootState } from 'old-store/store';
import { setCookie } from 'helpers/cookie';
import { saveEndCustomer } from 'store/slices/endCustomer';
import { getCollectionById, getEndCustomer, trackEventAction } from 'old-store/actions';
import { setCheckOutFlowAction } from '../checkout-flow';
import { getEndCustomerData } from 'old-store/selectors/endCustomer';
import { getCheckoutLoginForm } from '../checkout-flow/selectors';
import TagManager from 'react-gtm-module';
import { find } from 'lodash';
import { fetchMessage } from 'store/slices/messages/actions';
import { getShippingValuesFromCustomer } from 'modules/ScrShopNew/helpers/shipping';
import { toast } from 'react-toastify';
import { processBillingAddress } from '../../utils/helpers/validation';
import db from 'database';
import { setImages } from 'store/slices/images';
import { fetchPhotobooks } from 'modules/ScrPhotobook/store/slices/photobook/actions';
import { OrderStatus, PaymentMethod } from 'constants/payment';
import { saveAndSetNewToken } from 'helpers/tokens';
import { updateCheckoutSummaryUIAction, updateCheckoutStoreAction } from '../checkout';
import { convertToOrderSuccessAction } from '../orders';

const trackAddShippingInfo = (products: ICheckoutProduct[]) => {
  const isTrackAction =
    window.shopDataLayer && find(window.shopDataLayer, { event: 'add_shipping_info' });
  if (!isTrackAction) {
    trackProducts(products, 'add_shipping_info', {}, {});
  }
};

export const createUpdateCustomer = createAsyncThunk(
  'customer/createOrUpdate',
  async (payload: UpdateCreateCustomerPayload, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { _id, type } = getEndCustomerData(state);
    const shippingData = { ...getShippingStore(state) } as IShippingReducer;
    const products = getCheckoutProducts(state);

    Object.keys(shippingData).forEach((key) => {
      if (payload?.ignoreGtcAndPrivacyCheckbox && key === 'gtcAndPrivacyCheckbox') {
        shippingData[key] = { ...shippingData[key], error: '' };
      }

      const keysToExclude = ['gtcAndPrivacyCheckbox', 'billingAddress', 'isSameBillingAddress'];

      if (!keysToExclude.includes(key)) {
        shippingData[key] = { ...shippingData[key], value: shippingData[key]?.value?.trim() };
      }

      // @ts-ignore
      const error = validateFieldStore(key, shippingData[key], shippingData);
      if (error) {
        shippingData[error.name] = { ...shippingData[error.name], error: error.errorText };
      }
    });

    const hasError = isFormHasError(shippingData, payload?.excludeValidationFields);
    dispatch(setShippingValueAction(shippingData));

    if (hasError) {
      window.scrollTo(0, 0);

      return;
    }

    const formValues = {
      collection_id: window.SITE_ID,
      ...getFormValues(shippingData)
    };

    try {
      // @ts-ignore
      const response = await Api.Auth.updateOrCreateCustomer(formValues);
      ApiErrors.checkOnApiError(response);
      const { endCustomerToken, endCustomer, isNew } = response;
      setCookie('endCustomerToken', endCustomerToken, 30);
      dispatch(saveEndCustomer({ ...endCustomer, authed: true }));

      const { _user } = state.collection;
      localStorage.setItem('endCustomerToken', JSON.stringify({ [_user]: endCustomerToken }));

      if (isNew) {
        dispatch(
          trackEventAction({
            name: 'user-signed-up',
            payload: { endCustomer, flyUserId: type === 'fly' && _id }
          })
        );
      }

      const checkoutId = getCheckoutIDSelector(state);

      if (!payload?.withoutCheckoutFlow) {
        dispatch(claimCheckoutAction());
        dispatch(
          setCheckOutFlowAction({
            isShowShippingForm: false,
            isShowShippingEditForm: false,
            isShowLoginForm: false,
            isShowOverview: true
          })
        );

        dispatch(recalculatePricingAction({ cartId: checkoutId }));
        payload?.customerUpdateCallback?.();
      } else if (checkoutId) {
        // Recalculate pricing even when in edit mode on shipping page
        dispatch(recalculatePricingAction({ cartId: checkoutId }));
      }

      trackAddShippingInfo(products);
      payload?.submitCallback?.();

      if (payload?.showSuccessMessage) {
        toast.success(getTranslationKey('shop.shippingSections.accountUpdate'), {
          theme: 'colored'
        });
      }
    } catch (e) {
      console.error(e);

      // @ts-ignore
      if (e.code === 409) {
        dispatch(
          setShippingValueAction({
            email: {
              ...shippingData.email,
              error: getTranslationKey('shop.forms.backendEmailValidation')
            }
          })
        );
      }
    }
  }
);

export const updateBillingAddress = createAsyncThunk(
  'customer/updateBillingAddress',
  async (payload: UpdateBillingPayload, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const shippingData = { ...getShippingStore(state) } as IShippingReducer;
      const products = getCheckoutProducts(state);

      const billingAddress = shippingData.billingAddress.value as IBillingAddress;
      const updatedBillingAddress = processBillingAddress(billingAddress) as IBillingAddress;

      const updatedShippingData: IShippingReducer = {
        ...shippingData,
        billingAddress: {
          ...shippingData.billingAddress,
          value: updatedBillingAddress
        }
      };

      const hasError = isBillingFormHasError(updatedShippingData);
      dispatch(setShippingValueAction(updatedShippingData));

      if (hasError) return;

      const formValues = {
        collection_id: window.SITE_ID,
        ...getFormValues(updatedShippingData)
      };

      // @ts-ignore
      const response = await Api.Auth.updateOrCreateCustomer(formValues);
      ApiErrors.checkOnApiError(response);
      const { endCustomer } = response;
      dispatch(saveEndCustomer({ ...endCustomer, authed: true }));

      trackAddShippingInfo(products);
      payload?.submitCallback?.();

      if (payload?.showSuccessMessage) {
        toast.success(getTranslationKey('shop.shippingSections.accountUpdate'), {
          theme: 'colored'
        });
      }
    } catch (e) {
      console.error(e);
    }
  }
);

export const updateCustomerCountry = createAsyncThunk(
  'customer/updateCountry',
  async (payload: { country: string; context?: string }, { getState, dispatch }) => {
    const { country, context } = payload;
    const state = getState() as RootState;
    const shippingData = { ...getShippingStore(state) } as IShippingReducer;
    const checkoutId = getCheckoutIDSelector(state);

    // Set isPricingUpdating to true immediately before making the upsert-country request
    // This ensures the preloader is displayed right away
    if (context !== 'account' && checkoutId) {
      dispatch(setCheckOutFlowAction({ isPricingUpdating: true }));
    }

    // Update country in the form state
    dispatch(
      setShippingValueAction({
        _country: { ...shippingData._country, value: country, error: '' }
      })
    );

    const endCustomerEmail = getEndCustomerData(getState() as RootState)?.email;
    const currentFormEmail = shippingData.email?.value || endCustomerEmail;

    const formValues = {
      collection_id: window.SITE_ID,
      ...getFormValues(shippingData),
      _country: country,
      email: currentFormEmail
    };

    try {
      // @ts-ignore
      const response = await Api.Auth.updateEndCustomerCountry(formValues);

      // Check if this is a network error - if so, skip updating token
      // This prevents token mismatch issues when offline
      if (isNetworkError(response)) {
        console.log('Skipping country update due to network error');

        // Show offline error message
        toast.error(getTranslationKey('offlineMessage'), { theme: 'colored' });

        // Reset isPricingUpdating on network error
        if (context !== 'account' && checkoutId) {
          dispatch(setCheckOutFlowAction({ isPricingUpdating: false }));
        }

        return;
      }

      // If conflict (409), another user already has this email - reset country and show email error
      if (response?.code === 409) {
        dispatch(
          setShippingValueAction({
            _country: { ...shippingData._country, value: '', error: '' },
            email: {
              ...shippingData.email,
              error: getTranslationKey('shop.forms.backendEmailValidation')
            }
          })
        );

        if (context !== 'account' && checkoutId) {
          dispatch(setCheckOutFlowAction({ isPricingUpdating: false }));
        }

        return;
      }

      ApiErrors.checkOnApiError(response);
      const { endCustomerToken, endCustomer } = response;
      setCookie('endCustomerToken', endCustomerToken, 30);
      dispatch(saveEndCustomer({ ...endCustomer, authed: true }));

      const { _user } = state.collection;
      localStorage.setItem('endCustomerToken', JSON.stringify({ [_user]: endCustomerToken }));

      // Refresh products against the new tax context so any cart prune
      // below has up-to-date availability data.
      await dispatch(fetchProducts({ collection_id: window.SITE_ID }));

      // Recalculate pricing after country update, then prune any cart lines
      // whose product is no longer available for this customer.
      // isPricingUpdating will be set to false by recalculatePricingAction when it completes
      if (context !== 'account' && checkoutId) {
        await dispatch(recalculatePricingAction({ cartId: checkoutId }));
        await dispatch(pruneUnavailableCartItems());
      }
    } catch (e: any) {
      console.error('updateCustomerCountry error:', e);

      // Reset isPricingUpdating on error
      if (context !== 'account' && checkoutId) {
        dispatch(setCheckOutFlowAction({ isPricingUpdating: false }));
      }
    }
  }
);

// Email + password sign-in of the old gallery design (ShippingLoginModal).
export const loginCustomer = createAsyncThunk(
  'auth/loginCustomer',
  async ({ callback }: { callback: () => void }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const loginForm = getCheckoutLoginForm(state);
    const products = getCheckoutProducts(state);

    if (isFormHasError(loginForm)) return;

    const formValues = {
      collectionId: window.SITE_ID,
      ...getFormValues(loginForm)
    };

    try {
      // @ts-ignore
      await db.sites.delete(window.SITE_ID);
      const { _id, type } = getEndCustomerData(state);

      // @ts-ignore
      const response = await Api.Auth.login(formValues);
      ApiErrors.checkOnApiError(response);

      const { endCustomerToken, endCustomer } = response.result;
      const { _user } = state.collection;

      dispatch(saveEndCustomer({ ...endCustomer, authed: true }));
      saveAndSetNewToken(_user, endCustomerToken);

      dispatch(
        trackEventAction({
          name: 'user-signed-in',
          payload: { endCustomer, flyUserId: type === 'fly' && _id }
        })
      );

      const latestPayment = await Api.Orders.reFetchLatestPayment({
        collection_id: window.SITE_ID
      });

      if (latestPayment._order) {
        dispatch(updateCheckoutSummaryUIAction({ ...latestPayment._order }));
      }

      const hasPendingPayment = latestPayment?._order?.status === OrderStatus.PAYMENT_PENDING;
      const paymentMethod = getPaymentMethod(getState() as RootState);
      const isPaypalPendingPayment = hasPendingPayment && paymentMethod === PaymentMethod.PAYPAL;

      if (isPaypalPendingPayment) {
        // PayPal needs to complete the pending payment — restore order and redirect
        dispatch(
          convertToOrderSuccessAction({
            ...latestPayment._order,
            payments: [latestPayment]
          })
        );
        dispatch(
          updateCheckoutStoreAction({
            products: latestPayment._order.products,
            _id: latestPayment._order._shoppingCart,
            currency: latestPayment._order.currency
          })
        );
      } else {
        // No pending payment or non-PayPal — start fresh, claim and fetch active cart
        dispatch(claimCheckoutAction());
        await dispatch(fetchCheckoutAction());

        const updatedState = getState() as RootState;
        const checkoutId = getCheckoutIDSelector(updatedState);
        if (checkoutId) {
          dispatch(recalculatePricingAction({ cartId: checkoutId }));
        }
      }

      const isDigitalProduct = products.every((product) => product._productGroup === 'digital');

      dispatch(
        setCheckOutFlowAction({
          isShowLoginForm: false,
          isShowShippingForm: false,
          isShowShippingEditForm:
            !isDigitalProduct && !['germany', 'austria'].includes(endCustomer._country),
          isShowOverview: isDigitalProduct || ['germany', 'austria'].includes(endCustomer._country)
        })
      );

      dispatch(setShippingValueAction(getShippingValuesFromCustomer(endCustomer)));

      TagManager.dataLayer({
        dataLayer: { event: 'login' },
        dataLayerName: window.SHOP_DATA_LAYER_NAME
      });
      dispatch(fetchMessage());
      // we have to refetch images to synchronize user id
      const images = await Api.Images.getImages();
      dispatch(setImages({ images, signedWildCardUrl: state.collection.signedWildCardUrl }));
      (dispatch as any)(getCollectionById(window.SITE_ID));
      dispatch(fetchPhotobooks({}));
      (dispatch as any)(getEndCustomer({}));
      callback?.();
    } catch (e) {
      console.error(e);

      // @ts-ignore
      if (e.code === 404 || e.code === 403) {
        toast.error(getTranslationKey('shop.forms.backendLoginValidation'), {
          theme: 'colored',
          autoClose: 5000,
          hideProgressBar: true
        });
      }
    }
  }
);

// New design: the shop's part of a sign-in (one-time code or password, see useOtpLoginFlow) —
// the checkout skips the login step and starts from the customer's saved shipping data.
export const applyCheckoutAfterSignIn = createAsyncThunk(
  'auth/applyCheckoutAfterSignIn',
  async (endCustomer: any, { getState, dispatch }) => {
    const products = getCheckoutProducts(getState() as RootState);
    const isDigitalProduct = products.every((product) => product._productGroup === 'digital');
    const shipsDomestically = ['germany', 'austria'].includes(endCustomer?._country);

    dispatch(
      setCheckOutFlowAction({
        isShowLoginForm: false,
        isShowShippingForm: false,
        isShowShippingEditForm: !isDigitalProduct && !shipsDomestically,
        isShowOverview: isDigitalProduct || shipsDomestically
      })
    );
    dispatch(setShippingValueAction(getShippingValuesFromCustomer(endCustomer)));
    dispatch(fetchPhotobooks({}));
    (dispatch as any)(getEndCustomer({}));
  }
);
